import { useCallback, useMemo } from 'react'
import { useManagedUrlSearchParams } from 'hooks/useManagedUrlSearchParams'

export const TAB_QUERY_PARAM = 'tab'
const SCOPED_TAB_PARAMS = [TAB_QUERY_PARAM]

/**
 * Keeps a selected tab synchronized with the URL.
 *
 * Tab display titles should be unique. The first tab is used when the URL does
 * not contain a tab or contains a tab that is not available.
 *
 * Display titles are intentionally stored in the URL. If tab titles become
 * localized independently of the page URL, use a stable locale-independent
 * value here to preserve selection across locale changes.
 */
export const useTabQueryParam = ({ tabs = [], enabled = true } = {}) => {
  const tabValues = useMemo(() => tabs.map(tab => tab.displayTitle), [tabs])
  const defaultTabValue = tabValues[0]

  const defaults = useMemo(
    () =>
      enabled && defaultTabValue ? { [TAB_QUERY_PARAM]: defaultTabValue } : {},
    [defaultTabValue, enabled],
  )

  const validateParam = useCallback(
    (_key, value) => (tabValues.includes(value) ? value : defaultTabValue),
    [tabValues, defaultTabValue],
  )

  const { managedParams, updateUrlParams } = useManagedUrlSearchParams({
    defaults,
    scopedParams: SCOPED_TAB_PARAMS,
    validateParam,
    isInitializing: false,
    isSettled: true,
  })

  const selectedTabIndex = tabValues.indexOf(managedParams[TAB_QUERY_PARAM])
  const activeTabIndex = selectedTabIndex >= 0 ? selectedTabIndex : 0

  const selectTab = useCallback(
    tabIndex => {
      const tabValue = tabValues[tabIndex]
      if (!enabled || !tabValue) return

      updateUrlParams({ [TAB_QUERY_PARAM]: tabValue })
    },
    [tabValues, enabled, updateUrlParams],
  )

  return {
    activeTabIndex,
    activeTabValue: tabValues[activeTabIndex],
    selectTab,
  }
}
